import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Signal,
  SimpleChanges
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AWSRegion,
  FirmwareVersion,
  Gateway,
  GatewaySnap,
  GatewayType,
  GatewayVersion,
  LogLevel,
  NetworkType
} from '@app/api/building/gateway/Gateway';
import { GatewayHealth, GatewayService } from '@app/shared/services/gateway.service';
import { Observable } from 'rxjs';
import { AuthorizationModule } from '@app/shared/directives/authorization.module';
import { CreateFormFromType } from '@app/shared/models/types/utility-types';
import { MatInput, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatMiniFabButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCheckbox } from '@angular/material/checkbox';
import { GlobalAuthority } from '@app/shared/models/global-authority';
import { MatChip } from '@angular/material/chips';
import { GatewayHealthStatusComponent } from '@components/gateway-health-status/gateway-health-status.component';

type GatewayFormType = CreateFormFromType<Gateway>;

@Component({
  selector: 'app-form-gateway-details',
  templateUrl: './form-gateway-details.component.html',
  styleUrls: ['./form-gateway-details.component.scss'],
  imports: [
    ReactiveFormsModule,
    AuthorizationModule,
    MatInput,
    MatSelect,
    MatOption,
    MatIconModule,
    MatMiniFabButton,
    MatTooltip,
    MatCheckbox,
    MatLabel,
    MatChip,
    GatewayHealthStatusComponent
  ]
})
export class FormGatewayDetailsComponent implements OnInit, OnChanges {
  constructor(
    private fb: FormBuilder,
    private readonly gatewayService: GatewayService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    effect(() => {
      this.disableFormControls();
    });
  }
  @Input() public isNew: boolean;
  @Input() isBusy!: Signal<boolean>;

  @Input() public gateway: Gateway;

  @Input() public afterUpdate$: Observable<Gateway>;
  @Input() public error$: Observable<Gateway>;
  @Input() public onGatewayReset$: Observable<number>;

  @Output() deleteGateway = new EventEmitter<number>();
  @Output() resetGateway = new EventEmitter<number>();
  @Output() addGateway = new EventEmitter<Gateway>();
  @Output() updateGateway = new EventEmitter<Gateway>();
  @Output() getGatewaySnap = new EventEmitter<Gateway>();
  @Output() compareWithRigado = new EventEmitter<Gateway>();
  @Output() restartGateway = new EventEmitter<Gateway>();
  @Output() generateKeys = new EventEmitter<Gateway>();
  @Output() compareGatewayStates = new EventEmitter<Gateway>();
  @Output() gatewayMigration = new EventEmitter<Gateway>();

  gatewayHealth?: GatewayHealth;
  wirepas = NetworkType.WIREPAS;
  vesta = GatewayType.VESTA;
  gatewayForm: FormGroup<GatewayFormType>;
  isViewEdit = false;
  pattern = /^([a-fA-F\d]{8})-?([a-fA-F\d]{4})-?([a-fA-F\d]{4})-?([a-fA-F\d]{4})-?([a-fA-F\d]{12})$/;
  namePattern = /^[\da-zA-Z-_'\s]{0,256}$/;
  serialNumberPattern = /^[\da-zA-Z-_\s]{16,256}$/;
  ipAddressPattern = /^(?:\d{1,3}[.]){3}\d{1,3}$/;
  dnsServersPattern = /^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(\s*,\s*(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}))?$/;

  private visibleFields: string[];

  dynamicFieldsConfig = [
    {
      controlName: 'staticIpAddress',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value &&
        !this.gatewayForm.get('isDefaultNetwork').value
    },
    {
      controlName: 'gatewayIpAddress',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value &&
        !this.gatewayForm.get('isDefaultNetwork').value
    },
    {
      controlName: 'subnetMask',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value &&
        !this.gatewayForm.get('isDefaultNetwork').value
    },
    {
      controlName: 'dnsServers',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value &&
        !this.gatewayForm.get('isDefaultNetwork').value &&
        !this.gatewayForm.get('isDefaultDns').value
    },
    {
      controlName: 'bacnetDeviceId',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value &&
        this.gatewayForm.get('isBacnetEnabled').value
    },
    {
      controlName: 'bacnetPort',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value &&
        this.gatewayForm.get('isBacnetEnabled').value
    },
    {
      controlName: 'bacnetLogLevel',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value &&
        this.gatewayForm.get('isBacnetEnabled').value
    },
    {
      controlName: 'iotLogLevel',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value &&
        this.gatewayForm.get('isAwsIotEnabled').value
    },
    {
      controlName: 'otapLogLevel',
      isRequired: () =>
        this.gatewayForm.get('networkType').value === this.wirepas.value && this.gatewayForm.get('isOtapEnabled').value
    }
    // -- WNT Log level feature is disabled till Gateway Snap issue resolved
    // {
    //   controlName: 'wntLogLevel',
    //   isRequired: () =>
    //     this.gatewayForm.get('networkType').value === this.wirepas.value && this.gatewayForm.get('isWntEnabled').value
    // }
  ];

  protected readonly GlobalAuthority = GlobalAuthority;

  isBusyOrInvalidOrPristine(): boolean {
    return this.isBusy() || this.gatewayForm.invalid || this.gatewayForm.pristine;
  }

  isBusyOrInvalid(): boolean {
    return this.isBusy() || this.gatewayForm.invalid;
  }

  gatewayCanBeConfigured(): boolean {
    const gatewaySnap = this.gatewayForm.get('gatewaySnap')?.value;
    return this.isBusyOrInvalid() || !(gatewaySnap && gatewaySnap.length > 0) || !this.isSelectedFirmwareVersionAvailable();
  }

  private disableFormControls(): void {
    Object.keys(this.gatewayForm.controls).forEach((controlName) => {
      const control = this.gatewayForm.get(controlName);
      if (this.shouldDisableControl(controlName)) {
        control.disable();
      } else {
        control.enable();
      }
    });
  }

  ngOnInit(): void {
    this.gateway.isDefaultDns = !this.gateway.dnsServers;
    this.gateway.original = Gateway.clone(this.gateway);

    this.initializeForm();

    this.gatewayForm.get('address').valueChanges.subscribe(() => {
      this.onAddressChange();
    });

    this.afterUpdate$.subscribe((gateway) => {
      if (gateway.id === this.gateway.id) {
        this.gatewayForm.reset(this.gateway, { emitEvent: true });
        if (gateway.gatewaySnap !== this.gatewayForm.get('original').value.gatewaySnap) {
          this.onGatewaySnapChange();
        } else {
          this.ensureSelectedFirmwareVersionIsAvailable();
        }
      }
    });

    this.error$.subscribe((gateway) => {
      if (gateway.id === this.gateway.id) {
        this.isViewEdit = true;
      }
    });

    this.onGatewayReset$.subscribe((gatewayId: number) => {
      if (gatewayId === this.gateway.id) {
        this.resetGatewayForm();
      }
    });

    this.fetchHealth();

    [
      'networkType',
      'isDefaultNetwork',
      'isDefaultDns',
      'isBacnetEnabled',
      'isAwsIotEnabled',
      'isOtapEnabled',
      'isWntEnabled'
    ].forEach((fieldName) => {
      this.gatewayForm.get(fieldName)?.valueChanges.subscribe(() => {
        this.updateDynamicFieldValidators();
      });
    });
    this.updateDynamicFieldValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.gateway) {
      this.resetGatewayForm();
    }
  }

  private shouldDisableControl(controlName: string): boolean {
    switch (controlName) {
      case 'serialNumber':
      case 'gatewayType':
        return !this.isNew || this.isBusy();
      case 'address':
        return this.gateway.gatewayType !== this.vesta.value || this.isBusy();
      case 'networkType':
        return this.isBusy() || this.gateway.networkType === this.wirepas.value;
      case 'firmwareVersionId':
        return (
          (this.gateway.gatewaySnap !== GatewaySnap.ORT_WIREPAS &&
            this.gateway.gatewaySnap !== GatewaySnap.RIGADO_WIREPAS) ||
          this.isBusy()
        );
      default:
        return this.isBusy();
    }
  }

  private initializeForm(): void {
    this.gatewayForm = this.fb.group<GatewayFormType>({
      original: new FormControl(this.gateway.original),
      name: new FormControl(this.gateway.name, [Validators.required, Validators.pattern(this.namePattern)]),
      serialNumber: new FormControl(this.gateway.serialNumber, [
        Validators.required,
        Validators.pattern(this.serialNumberPattern)
      ]),
      address: new FormControl(this.gateway.address, [Validators.required, Validators.pattern(this.pattern)]),
      buildingId: new FormControl(this.gateway.buildingId),
      id: new FormControl(this.gateway.id),
      versionNumber: new FormControl(this.gateway.versionNumber, [Validators.required]),
      isDefaultNetwork: new FormControl(this.gateway.isDefaultNetwork),
      isActive: new FormControl(this.gateway.isActive),
      staticIpAddress: new FormControl(this.gateway.staticIpAddress, [Validators.pattern(this.ipAddressPattern)]),
      gatewayIpAddress: new FormControl(this.gateway.gatewayIpAddress, [Validators.pattern(this.ipAddressPattern)]),
      isDefaultDns: new FormControl(this.gateway.isDefaultDns),
      dnsServers: new FormControl(this.gateway.dnsServers, [Validators.pattern(this.dnsServersPattern)]),
      subnetMask: new FormControl(this.gateway.subnetMask, [Validators.pattern(this.ipAddressPattern)]),
      networkType: new FormControl(this.gateway.networkType, [Validators.required]),
      firmwareVersionId: new FormControl(this.gateway.firmwareVersionId, [Validators.required]),
      gatewaySnap: new FormControl(this.gateway.gatewaySnap),
      gatewayType: new FormControl(this.gateway.gatewayType, [Validators.required]),
      gatewayHealthLogLevel: new FormControl(this.gateway.gatewayHealthLogLevel),
      isAwsIotEnabled: new FormControl(this.gateway.isAwsIotEnabled),
      iotLogLevel: new FormControl(this.gateway.iotLogLevel),
      isOtapEnabled: new FormControl(this.gateway.isOtapEnabled),
      otapLogLevel: new FormControl(this.gateway.otapLogLevel),
      isWntEnabled: new FormControl(this.gateway.isWntEnabled),
      wntLogLevel: new FormControl(this.gateway.wntLogLevel),
      isBacnetEnabled: new FormControl(this.gateway.isBacnetEnabled),
      bacnetDeviceId: new FormControl(this.gateway.bacnetDeviceId),
      bacnetPort: new FormControl(this.gateway.bacnetPort),
      bacnetLogLevel: new FormControl(this.gateway.bacnetLogLevel),
      hasKeys: new FormControl(this.gateway.hasKeys),
      lastRestart: new FormControl(this.gateway.lastRestart),
      lastUpdated: new FormControl(this.gateway.lastUpdated),
      hasMatchingBuildingAWSRegion: new FormControl(this.gateway.hasMatchingBuildingAWSRegion)
    });
  }

  private updateDynamicFieldValidators(): void {
    this.dynamicFieldsConfig.forEach((field) => {
      const control = this.gatewayForm.get(field.controlName);
      if (control) {
        if (field.isRequired() && !control.hasValidator(Validators.required)) {
          control.setValidators([Validators.required]);
        } else if (!field.isRequired()) {
          control.clearValidators();
        }
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  private updateGatewayWithFormValues(): void {
    Object.keys(this.gatewayForm.controls).forEach((controlName) => {
      if (this.gatewayForm.get(controlName)) {
        this.gateway[controlName] = this.gatewayForm.get(controlName).value;
      }
    });
  }

  private fetchHealth(): void {
    if (this.gateway.address != null) {
      this.gatewayService.getGatewayHealth(this.gateway.address).subscribe((health) => {
        this.gatewayHealth = health;
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  public onAddressChange(): void {
    const groups = this.pattern.exec(this.gatewayForm.get('address').value);
    if (groups != null && groups.length > 5) {
      this.gatewayForm.get('address').setValue(
        groups[1].toLowerCase() +
          '-' +
          groups[2].toLowerCase() +
          '-' +
          groups[3].toLowerCase() +
          '-' +
          groups[4].toLowerCase() +
          '-' +
          groups[5].toLowerCase(),
        { emitEvent: false } // Prevent triggering valueChanges
      );
    }
  }

  public onGatewaySnapChange(): void {
    if (!this.gateway.gatewaySnap) {
      return;
    }

    const availableFirmwareVersions = this.getAllFirmwareVersions();
    const firmwareVersionId = availableFirmwareVersions.length > 0 ? availableFirmwareVersions.slice(-1)[0].id : null;
    this.gatewayForm.get('firmwareVersionId').setValue(firmwareVersionId);
    this.gatewayForm.get('firmwareVersionId').updateValueAndValidity({ onlySelf: true });
    this.gatewayForm.markAsDirty();
  }

  public hasKeys(): boolean {
    return this.gateway.hasKeys;
  }

  public onAddGateway(): void {
    if (this.gatewayForm.valid) {
      this.updateGatewayWithFormValues();
      this.addGateway.emit(this.gateway);
    }
  }

  public onUpdateGateway(): void {
    if (this.gatewayForm.valid) {
      this.updateGatewayWithFormValues();
      this.updateGateway.emit(this.gateway);
    }
  }

  public onEdit(): void {
    if (!this.isViewEdit) {
      this.getGatewaySnap.emit(this.gateway);
    }
    this.isViewEdit = !this.isViewEdit;
    this.resetHiddenFields();
  }

  public onCompareWithRigado(): void {
    if (this.gatewayForm.valid) {
      this.updateGatewayWithFormValues();
      this.compareWithRigado.emit(this.gateway);
    }
  }

  public onCompareGatewayStates(): void {
    if (this.gatewayForm.valid) {
      this.updateGatewayWithFormValues();
      this.compareGatewayStates.emit(this.gateway);
    }
  }

  public resetGatewayForm(): void {
    if (this.gatewayForm) {
      this.gatewayForm.reset(this.gateway);
      this.disableFormControls();
    }
  }

  public onRestartGateway(): void {
    if (this.gatewayForm.valid) {
      this.updateGatewayWithFormValues();
      this.restartGateway.emit(this.gateway);
    }
  }

  public onGenerateKeys(): void {
    if (this.gatewayForm.valid) {
      this.updateGatewayWithFormValues();
      this.generateKeys.emit(this.gateway);
    }
  }

  public onResetGateway(): void {
    this.resetGateway.emit(this.gateway.id);
  }

  public onDelete(): void {
    this.deleteGateway.emit(this.gateway.id);
  }

  public migrateGateway() {
    if (this.gatewayForm.valid) {
      this.gatewayMigration.emit(this.gateway);
    }
  }

  public getAllNetworkTypes(): NetworkType[] {
    return NetworkType.all();
  }

  public getAllFirmwareVersions(): FirmwareVersion[] {
    const firmwareVersions =
      this.gatewayForm.get('gatewaySnap').value === GatewaySnap.ORT_WIREPAS
        ? FirmwareVersion.all()
        : this.getLegacySupportedFirmwareVersions();
    return firmwareVersions.filter((version) => this.isFirmwareVersionVisible(version));
  }

  public getLegacySupportedFirmwareVersions(): FirmwareVersion[] {
    return FirmwareVersion.all().filter((version) => version.legacySupport);
  }

  public getAllGatewayTypes(): GatewayType[] {
    return GatewayType.all();
  }

  public getAllGatewayVersions(): GatewayVersion[] {
    return GatewayVersion.all();
  }

  public onOptionsSelected(event) {
    const value = event.value;
    if (value === GatewayVersion.VERSIONTWO.value) {
      if (
        confirm(
          'All gateways in the building need to be either Version 2 or Version 3 for Portal to work as expected, are you sure you want to select Version 2?'
        )
      ) {
        this.gatewayForm.get('versionNumber').setValue(GatewayVersion.VERSIONTWO.value);
      } else {
        this.gatewayForm.get('versionNumber').setValue(GatewayVersion.VERSIONTHREE.value);
      }
    }
  }

  public getAllLogLevels(): LogLevel[] {
    return LogLevel.all();
  }

  public resetHiddenFields(): void {
    const controls = { ...this.gatewayForm.controls };
    if (this.isViewEdit) {
      this.visibleFields = [];
      for (const attr in controls) {
        if (Object.prototype.hasOwnProperty.call(controls, attr)) {
          this.visibleFields.push(attr);
        }
      }
    } else {
      const fieldsToReset = [];
      for (const attr in controls) {
        if (!this.visibleFields.includes(attr)) {
          fieldsToReset.push(attr);
        }
      }
      this.updateToDefault(fieldsToReset);
    }
  }

  public updateToDefault(attrs: string[], shouldApplyValidators = false): void {
    for (const attr of attrs) {
      this.gatewayForm.get(attr).setValue(this.gateway.original?.[attr]);
    }
  }

  private ensureSelectedFirmwareVersionIsAvailable(): void {
    if (this.gatewayForm.get('gatewaySnap').value == null) {
      return;
    }

    if (!this.isSelectedFirmwareVersionAvailable()) {
      this.onGatewaySnapChange();
    }
  }

  private isSelectedFirmwareVersionAvailable(): boolean {
    const selectedFirmwareVersionId = this.gatewayForm.get('firmwareVersionId').value;
    if (selectedFirmwareVersionId == null) {
      return false;
    }

    return this.getAllFirmwareVersions().some((firmwareVersion) => firmwareVersion.id === selectedFirmwareVersionId);
  }

  private isFirmwareVersionVisible(firmwareVersion: FirmwareVersion): boolean {
    return !firmwareVersion.nmsSupport || this.supportsNmsFirmware();
  }

  private supportsNmsFirmware(): boolean {
    return (
      this.gateway.gatewaySnap === GatewaySnap.ORT_WIREPAS &&
      this.gateway.gatewayType === GatewayType.GATEWAYONEPOINTTWO.value &&
      this.gateway.supportsNmsFirmware === true
    );
  }

  public resetNetworkFields(): void {
    this.updateToDefault(['staticIpAddress', 'gatewayIpAddress', 'subnetMask', 'isDefaultDns', 'dnsServers']);
  }

  public resetBacnetFields(): void {
    this.updateToDefault(['bacnetDeviceId', 'bacnetPort', 'bacnetLogLevel']);
  }

  resetDnsFields(): void {
    this.updateToDefault(['dnsServers']);
  }

  get gatewayRegion(): string | null {
    return this.gateway.awsRegion ? AWSRegion.fromCode(this.gateway.awsRegion)?.getName() : null;
  }
}
